import { Button, useAlert } from '@ads/components-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParcelActionRunner } from '@/features/individual-payment/hooks/useParcelActionRunner';
import { useParcelCreateLookups } from '@/features/individual-payment/hooks/useParcelCreateLookups';
import type { ParcelEditFormValues } from '@/features/individual-payment/hooks/useParcelEditFormSchema';
import { parcelEditSchema } from '@/features/individual-payment/hooks/useParcelEditFormSchema';
import { useParcelEditSeed } from '@/features/individual-payment/hooks/useParcelEditSeed';
import { useParcelEnums } from '@/features/individual-payment/hooks/useParcelEnums';
import { useParcelInvoices } from '@/features/individual-payment/hooks/useParcelInvoices';
import { useUpdateParcel } from '@/features/individual-payment/hooks/useUpdateParcel';
import type { PaymentHistoryMode } from '@/features/individual-payment/types/paymentHistory';
import { ParcelActionConfirmModal } from '@/features/individual-payment/ui/organisms/ParcelActionConfirmModal';
import { ParcelAuditSection } from '@/features/individual-payment/ui/organisms/ParcelAuditSection';
import { ParcelBilletHistoryDrawer } from '@/features/individual-payment/ui/organisms/ParcelBilletHistoryDrawer';
import { ParcelCommunicationSection } from '@/features/individual-payment/ui/organisms/ParcelCommunicationSection';
import { ParcelContractCard } from '@/features/individual-payment/ui/organisms/ParcelContractCard';
import type { ParcelEditToolbarAction } from '@/features/individual-payment/ui/organisms/ParcelEditToolbar';
import { ParcelEditToolbar } from '@/features/individual-payment/ui/organisms/ParcelEditToolbar';
import { ParcelHistoryDrawer } from '@/features/individual-payment/ui/organisms/ParcelHistoryDrawer';
import { ParcelIdentificationSection } from '@/features/individual-payment/ui/organisms/ParcelIdentificationSection';
import { ParcelInvoiceSection } from '@/features/individual-payment/ui/organisms/ParcelInvoiceSection';
import { ParcelInvoicesTable } from '@/features/individual-payment/ui/organisms/ParcelInvoicesTable';
import { ParcelInvoiceUploadModal } from '@/features/individual-payment/ui/organisms/ParcelInvoiceUploadModal';
import { ParcelValuesSection } from '@/features/individual-payment/ui/organisms/ParcelValuesSection';
import { ParcelEditTemplate } from '@/features/individual-payment/ui/templates/ParcelEditTemplate';
import { buildParcelEditPayload } from '@/features/individual-payment/utils/buildParcelEditPayload';
import { formatContractDate } from '@/features/individual-payment/utils/formatContractDate';
import { EMPTY_VALUE } from '@/features/individual-payment/utils/formatCurrency';
import type { PlanWriteError } from '@/features/individual-payment/utils/planWriteError';
import { Skeleton } from '@/shared/ui/atoms';

export const Route = createFileRoute(
  '/_authenticated/individual-payment/$financialPersonId/parcels/$parcelId/edit/',
)({
  component: ParcelEditPage,
});

/** Converte um parâmetro de rota em id, ou `null` quando não é inteiro positivo. */
const toId = (raw: string): number | null => {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Página de edição de uma parcela de um contrato financeiro.
 *
 * Orquestra semeadura, formulário, seções, painel de histórico e escrita.
 *
 * **A gravação é liberada** desde que `GET /parcels/{parcelId}` passou a devolver
 * exatamente os campos que o `PUT` aceita: o formulário conhece o valor atual de
 * cada um, então o full-replace do endpoint não apaga nada que o operador não
 * tenha editado.
 *
 * O Salvar continua derivado de `seed.isComplete` — nada aqui decide por conta
 * própria. Ele fica desabilitado enquanto a semeadura não trouxe a parcela, que é
 * a única condição em que gravar seria destrutivo.
 */
export function ParcelEditPage() {
  const { t } = useTranslation('individualPayment');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { financialPersonId: rawContractId, parcelId: rawParcelId } = Route.useParams();

  // Os parâmetros de rota são string. Converter aqui, na fronteira, evita propagar
  // `NaN` para a URL das requisições de apoio.
  const financialPersonId = useMemo(() => toId(rawContractId), [rawContractId]);
  const parcelId = useMemo(() => toId(rawParcelId), [rawParcelId]);

  const {
    header,
    isHeaderError,
    refetchHeader,
    paymentMethods,
    arePaymentMethodsLoading,
    hasNoPaymentMethod,
    planPaymentTypeLabel,
  } = useParcelCreateLookups(financialPersonId);

  const seed = useParcelEditSeed(parcelId);
  const invoices = useParcelInvoices(parcelId);
  const { types, isLoading: areTypesLoading } = useParcelEnums(parcelId !== null);

  const form = useForm<ParcelEditFormValues>({
    resolver: zodResolver(parcelEditSchema),
    defaultValues: seed.values as ParcelEditFormValues,
  });

  // A semeadura chega depois do primeiro render quando a coleção não está em
  // cache — quem entra pela URL direta, por exemplo. `reset` reaproveita o mesmo
  // formulário em vez de remontá-lo, preservando o estado de toque dos campos.
  const { reset } = form;
  useEffect(() => {
    if (seed.parcel === undefined) return;
    reset(seed.values as ParcelEditFormValues);
  }, [reset, seed.parcel, seed.values]);

  // Painel de histórico da parcela, aberto pelas duas ações de leitura da régua.
  const [history, setHistory] = useState<PaymentHistoryMode | null>(null);

  // As outras duas ações da régua têm camada própria. São três estados separados
  // porque as três camadas se excluem, mas nenhuma delas depende das outras.
  const [isBilletHistoryOpen, setBilletHistoryOpen] = useState(false);
  const [isUploadOpen, setUploadOpen] = useState(false);

  // Boleto aguardando confirmação de reintegração. `undefined` no lugar do serial
  // é um alvo válido — o endpoint aceita a reintegração sem boleto escolhido —,
  // então o estado é um objeto, e não o serial solto, para distinguir "fechado"
  // de "aberto sem serial".
  const [reintegrateTarget, setReintegrateTarget] = useState<{
    readonly serial?: number | undefined;
  } | null>(null);

  const { update, isPending } = useUpdateParcel(financialPersonId, parcelId);

  const {
    run: runAction,
    reset: resetAction,
    isPending: isActionPending,
    error: actionError,
  } = useParcelActionRunner(financialPersonId);

  const backToParcels = () =>
    void navigate({
      to: '/individual-payment/$financialPersonId/parcels',
      params: { financialPersonId: rawContractId },
    });

  /**
   * Anuncia a recusa da gravação em toast, com o motivo que o backend deu.
   *
   * A mensagem do backend é o conteúdo principal — é ela que diz o que fazer
   * ("Esta ação não é permitida.") — e o título só a enquadra. Sem mensagem, o
   * status entra no lugar, para o operador ter o que relatar.
   */
  const alertWriteFailure = (failure: PlanWriteError) => {
    const description =
      failure.message ??
      (failure.status === null
        ? t('parcelEdit.updateErrorFallback')
        : t('parcelEdit.updateErrorFallbackWithStatus', { status: failure.status }));

    alert({
      alertType: failure.kind === 'server' ? 'danger' : 'warning',
      title: t('parcelEdit.updateError'),
      description,
    });
  };

  const handleSave = form.handleSubmit(values => {
    if (parcelId === null) return;

    update(buildParcelEditPayload({ parcelId, values, unseededFields: seed.unseededFields }), {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('parcelEdit.updateSuccess') });
        backToParcels();
      },
      onError: alertWriteFailure,
    });
  });

  /** As quatro ações da régua: duas abrem histórico, duas abrem camada própria. */
  const handleToolbarAction = (action: ParcelEditToolbarAction) => {
    if (action === 'orderTracking' || action === 'traceability') {
      setHistory(action);
      return;
    }

    if (action === 'billetHistory') {
      setBilletHistoryOpen(true);
      return;
    }

    setUploadOpen(true);
  };

  if (financialPersonId === null || parcelId === null) {
    return (
      <PageFallback
        title={t('parcels.invalidId')}
        hint={t('parcels.invalidIdHint')}
        actionLabel={t('parcels.backToList')}
        onAction={() => void navigate({ to: '/individual-payment' })}
      />
    );
  }

  if (isHeaderError) {
    return (
      <PageFallback
        title={t('parcels.headerError')}
        hint={t('parcels.table.errorHint')}
        actionLabel={t('parcels.table.retry')}
        onAction={refetchHeader}
      />
    );
  }

  // Sem a coleção de parcelas não há o que semear: o formulário abriria vazio e
  // trocaria de valor sob o operador quando a request chegasse.
  if (seed.isError) {
    return (
      <PageFallback
        title={t('parcelEdit.seedError')}
        hint={t('parcels.table.errorHint')}
        actionLabel={t('parcels.table.retry')}
        onAction={seed.refetch}
      />
    );
  }

  if (seed.isNotFound) {
    return (
      <PageFallback
        title={t('parcelEdit.notFound')}
        hint={t('parcelEdit.notFoundHint')}
        actionLabel={t('parcelEdit.backToParcels')}
        onAction={backToParcels}
      />
    );
  }

  const parcelNumber = seed.parcel?.numberParcel;

  /** Contexto repetido no subtítulo das duas camadas da régua. */
  const parcelScope = t('parcelEdit.parcelScope', {
    number: parcelNumber ?? EMPTY_VALUE,
    student: header?.studentName ?? EMPTY_VALUE,
  });

  return (
    <ParcelEditTemplate
      financialPersonId={rawContractId}
      studentName={header?.studentName ?? EMPTY_VALUE}
      statusLabel={seed.parcel?.statusText}
      subtitle={t('parcelEdit.subtitle', {
        number: parcelNumber ?? EMPTY_VALUE,
        type: seed.parcel?.typeText ?? EMPTY_VALUE,
        course: header?.courseName ?? EMPTY_VALUE,
      })}
      toolbar={
        <ParcelEditToolbar
          onAction={handleToolbarAction}
          lastIntegrationLabel={
            seed.parcel?.paymentDate ? formatContractDate(seed.parcel.paymentDate) : null
          }
        />
      }
      isSaving={isPending}
      canSave={seed.isComplete}
      // Durante a carga o corpo é skeleton: anunciar bloqueio ali afirmaria uma
      // limitação que não existe. A mensagem só aparece se a semeadura terminou
      // sem trazer a parcela.
      blockedReason={seed.isComplete || seed.isLoading ? undefined : t('parcelEdit.blockedReason')}
      onSave={() => void handleSave()}
      onCancel={backToParcels}
    >
      <ParcelContractCard
        header={header}
        isLoading={seed.isLoading}
        paymentTypeLabel={planPaymentTypeLabel}
      />

      {seed.isLoading ? (
        <SeedSkeleton />
      ) : (
        <>
          <ParcelIdentificationSection
            form={form}
            paymentMethods={paymentMethods}
            arePaymentMethodsLoading={arePaymentMethodsLoading}
            hasNoPaymentMethod={hasNoPaymentMethod}
            types={types}
            areTypesLoading={areTypesLoading}
            typeLabel={seed.parcel?.typeText ?? ''}
            statusLabel={seed.parcel?.statusText ?? ''}
            showSerial
          />

          <ParcelValuesSection
            form={form}
            disabledFields={seed.unseededFields}
            showEditOnlyFields
          />

          <ParcelInvoiceSection form={form} disabledFields={seed.unseededFields} />

          <ParcelCommunicationSection form={form} disabledFields={seed.unseededFields} />
        </>
      )}

      <ParcelInvoicesTable
        invoices={invoices.invoices}
        isLoading={invoices.isLoading}
        isError={invoices.isError}
        onRetry={invoices.refetch}
      />

      <ParcelAuditSection
        createdBy={seed.parcel?.userCreation}
        createdAt={seed.parcel?.creationDate}
        updatedBy={seed.parcel?.userLastChange}
        updatedAt={seed.parcel?.lastChangeDate}
      />

      <ParcelHistoryDrawer
        parcelId={history === null ? null : parcelId}
        mode={history}
        parcelNumber={parcelNumber}
        onClose={() => setHistory(null)}
      />

      <ParcelBilletHistoryDrawer
        parcelId={isBilletHistoryOpen ? parcelId : null}
        subtitle={parcelScope}
        onClose={() => setBilletHistoryOpen(false)}
        onReintegrate={serial => {
          resetAction();
          setReintegrateTarget({ serial });
        }}
      />

      <ParcelActionConfirmModal
        action={reintegrateTarget === null ? null : 'reintegrate'}
        parcel={seed.parcel}
        numberParcels={header?.numberParcels}
        serial={reintegrateTarget?.serial}
        isPending={isActionPending}
        error={actionError}
        onConfirm={() => {
          if (parcelId === null) return;

          runAction(
            {
              action: 'reintegrate',
              parcelId,
              ...(reintegrateTarget?.serial === undefined
                ? {}
                : { serial: reintegrateTarget.serial }),
            },
            {
              onSuccess: () => {
                setReintegrateTarget(null);
                resetAction();
                alert({ alertType: 'success', title: t('parcels.confirm.reintegrate.success') });
              },
            },
          );
        }}
        onClose={() => {
          setReintegrateTarget(null);
          resetAction();
        }}
      />

      {/* O diálogo fica montado e fecha por `parcelId: null` — desmontá-lo aberto
          deixaria o arquivo escolhido preso para a próxima abertura. */}
      <ParcelInvoiceUploadModal
        parcelId={isUploadOpen ? parcelId : null}
        subtitle={parcelScope}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          alert({ alertType: 'success', title: t('parcelEdit.upload.success') });
        }}
      />
    </ParcelEditTemplate>
  );
}

/** Estado de tela inteira — id inválido, erro de leitura ou parcela inexistente. */
function PageFallback({
  title,
  hint,
  actionLabel,
  onAction,
}: {
  readonly title: string;
  readonly hint: string;
  readonly actionLabel: string;
  readonly onAction: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xl">
      <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
        <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
      </span>
      <p className="ts-heading-xs text-ctx-content-title">{title}</p>
      <p className="ts-paragraph-xs text-ctx-content-placeholder">{hint}</p>
      <Button hierarchy="secondary" size="md" onClick={onAction}>
        {actionLabel}
      </Button>
    </div>
  );
}

/** Esqueleto das quatro seções do formulário enquanto a semeadura não chega. */
function SeedSkeleton() {
  return (
    <div className="flex flex-col gap-xs">
      {Array.from({ length: 4 }, (_, index) => `parcel-edit-skeleton-${index}`).map(key => (
        <div
          key={key}
          className="flex flex-col gap-xxxs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-xxs"
        >
          <Skeleton className="h-xs w-[12rem] rounded-sm" />
          <div className="flex flex-wrap gap-xxxs">
            <Skeleton className="h-xl flex-1 basis-64 rounded-sm" />
            <Skeleton className="h-xl flex-1 basis-64 rounded-sm" />
            <Skeleton className="h-xl flex-1 basis-64 rounded-sm" />
          </div>
        </div>
      ))}
    </div>
  );
}
